import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Calendar, Clock, MapPin, MessageSquare, Plus, Eye, Clock as ClockIcon } from 'lucide-react';
import OutingNavigation from './OutingNavigation';

const StudentOutingPortal = () => {
  const { user } = useAuth();
  const [outingRequests, setOutingRequests] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    requestType: 'outing',
    outingDate: '',
    returnDate: '',
    numberOfDays: 1,
    departureTime: '',
    returnTime: '',
    message: ''
  });

  useEffect(() => {
    if (user) {
      fetchOutingRequests();
    }
  }, [user]);

  const fetchOutingRequests = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/outing/requests?studentId=${user._id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOutingRequests(data.outingRequests || []);
      }
    } catch (error) {
      console.error('Error fetching outing requests:', error);
      setError('Failed to fetch outing requests');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/outing/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        setOutingRequests(prev => [result.outingRequest, ...prev]);
        setShowCreateForm(false);
        setFormData({
          requestType: 'outing',
          outingDate: '',
          returnDate: '',
          numberOfDays: 1,
          departureTime: '',
          returnTime: '',
          message: ''
        });
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to create outing request');
      }
    } catch (error) {
      console.error('Error creating outing request:', error);
      setError('Failed to create outing request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'class_teacher_approved': return 'bg-blue-100 text-blue-800';
      case 'warden_approved': return 'bg-indigo-100 text-indigo-800';
      case 'principal_approved': return 'bg-green-100 text-green-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'denied': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending Parent Permission';
      case 'class_teacher_approved': return 'Class Teacher Approved';
      case 'warden_approved': return 'Warden Approved';
      case 'principal_approved': return 'Principal Approved';
      case 'approved': return 'Approved';
      case 'denied': return 'Denied';
      default: return 'Unknown';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <OutingNavigation userRole="student" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Message */}
        <div className="mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Calendar className="h-6 w-6 text-blue-600" />
              <div>
                <h3 className="text-lg font-medium text-blue-900">
                  Welcome, {user.profile?.firstName || 'Student'}!
                </h3>
                <p className="text-blue-700">
                  You can create outing requests and track their approval status here.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Outing Management</h2>
              <p className="text-gray-600">Request and track your outing permissions</p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              New Outing Request
            </button>
          </div>
        </div>

        {/* Create Outing Request Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Create Outing Request</h3>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Request Type
                  </label>
                  <select
                    value={formData.requestType}
                    onChange={(e) => setFormData({...formData, requestType: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  >
                    <option value="outing">Regular Outing</option>
                    <option value="leave">Leave</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Days
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={formData.numberOfDays}
                    onChange={(e) => setFormData({...formData, numberOfDays: parseInt(e.target.value)})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Outing Date
                  </label>
                  <input
                    type="date"
                    value={formData.outingDate}
                    onChange={(e) => setFormData({...formData, outingDate: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Return Date
                  </label>
                  <input
                    type="date"
                    value={formData.returnDate}
                    onChange={(e) => setFormData({...formData, returnDate: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Departure Time
                  </label>
                  <input
                    type="time"
                    value={formData.departureTime}
                    onChange={(e) => setFormData({...formData, departureTime: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Return Time
                  </label>
                  <input
                    type="time"
                    value={formData.returnTime}
                    onChange={(e) => setFormData({...formData, returnTime: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message/Reason
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Please provide a reason for your outing request..."
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Request'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Outing Requests List */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">My Outing Requests</h3>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading outing requests...</p>
            </div>
          ) : outingRequests.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No outing requests found</p>
              <p className="text-sm text-gray-500">Create your first outing request to get started</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {outingRequests.map((request) => (
                <div key={request._id} className="p-6 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.approvalStatus)}`}>
                          {getStatusText(request.approvalStatus)}
                        </span>
                        <span className="text-sm text-gray-500">
                          Request ID: {request.requestId}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {new Date(request.outingDate).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {request.departureTime} - {request.returnTime}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <ClockIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {request.numberOfDays} day{request.numberOfDays > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>

                      {request.studentMessage && (
                        <div className="flex items-start space-x-2 mb-3">
                          <MessageSquare className="h-4 w-4 text-gray-400 mt-0.5" />
                          <p className="text-sm text-gray-600">{request.studentMessage}</p>
                        </div>
                      )}

                      {/* Approval Flow Status */}
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Approval Status:</h4>
                        <div className="flex space-x-4">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              request.approvalFlow?.classTeacher?.status === 'approved' ? 'bg-green-500' : 
                              request.approvalFlow?.classTeacher?.status === 'denied' ? 'bg-red-500' : 'bg-gray-300'
                            }`}></div>
                            <span className="text-xs text-gray-600">Class Teacher</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              request.approvalFlow?.warden?.status === 'approved' ? 'bg-green-500' : 
                              request.approvalFlow?.warden?.status === 'denied' ? 'bg-red-500' : 'bg-gray-300'
                            }`}></div>
                            <span className="text-xs text-gray-600">Warden</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              request.approvalFlow?.principal?.status === 'approved' ? 'bg-green-500' : 
                              request.approvalFlow?.principal?.status === 'denied' ? 'bg-red-500' : 'bg-gray-300'
                            }`}></div>
                            <span className="text-xs text-gray-600">Principal</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {/* TODO: View details modal */}}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentOutingPortal;
